/* eslint-disable react/no-unused-prop-types */
import {
    ReactNode, useCallback, useMemo,
    useState,
} from 'react';
import { useConnectModal, useOpenModal, useReduxForm } from '@rainbow-modules/hooks';
import { PicklistValue } from '@rainbow-modules/listview/lib/components/AuditLogs/types';
import { Option, RenderIf } from 'react-rainbow-components';
import { Customer } from 'data/firestore/agent/customer/types';
import getCustomer from 'data/firestore/agent/customer/get';
import getDisplayName from 'data/services/profile/getDisplayName';
import CreateEditCustomerForm from 'components/CreateEditCustomerForm';
import CirclePlusIcon from 'components/icons/plusCircle';
import PencilIcon from 'components/icons/pencil';
import ListTile from 'components/ListTile';
import UserAvatar from 'components/UserAvatar';
import useSearchCustomers from 'data/hooks/useSearchCustomers';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import getInitials from 'data/services/string/getInitials';
import { PAGE_SIZE } from '../../constants';
import SelectedValue from './value';
import {
    Container, StyledPicklist, StyledButton, StyledDrawer,
} from './styled';

interface CustomerPickerProps {
    id?: string;
    className?: string;
    agentId?: string;
    name?: string;
    label?: string;
    tabIndex?: number | string;
    placeholder?: string;
    error?: ReactNode;
    required?: boolean;
    disabled?: boolean;
    value?: string | PicklistValue;
    onChange?: (value?: Customer | null) => void;
    disableCreate?: boolean;
    enableEdit?: boolean;
    compact?: boolean;
}

const ADD_CUSTOMER_FORM_PREFIX = 'customer-picker-add-form';

const CustomerPicker = (props: CustomerPickerProps) => {
    const {
        value: valueInProps,
        agentId = '',
        onChange = () => {},
        id,
        className,
        name,
        label,
        tabIndex,
        placeholder,
        error,
        required,
        disabled,
        disableCreate = false,
        enableEdit = false,
        compact = false,
    } = useReduxForm(props);

    const connectedAddModifyProps = useConnectModal(
        `${ADD_CUSTOMER_FORM_PREFIX}__${name}`,
        { size: 'small', slideFrom: 'right' },
    );

    const [openDrawer, closeDrawer] = useOpenModal(`${ADD_CUSTOMER_FORM_PREFIX}__${name}`);
    const {
        mode, customerData = {}, onCompleted, ...drawerProps
    } = connectedAddModifyProps;

    const [search, setSearch] = useState<string>('');

    const { data: searchCustomersData, isLoading: isLoadingSearchCustomers } = useSearchCustomers({
        agentId,
        searchQuery: search,
        pageSize: PAGE_SIZE,
        page: 1,
    });

    const customers = useMemo(() => searchCustomersData?.hits || [], [searchCustomersData?.hits]);

    const pickListvalue = useMemo(() => {
        if (valueInProps) {
            let inputValue: Customer | null | undefined = valueInProps as Customer;
            if (typeof valueInProps === 'string') {
                inputValue = customers.find((customer) => customer.id === valueInProps);
            }
            if (inputValue) {
                const { id: customerId, phoneNumber, ...rest } = inputValue;
                const displayName = getDisplayName(rest);
                return {
                    label: displayName,
                    name: customerId,
                    value: inputValue,
                    searchableText: displayName,
                };
            }
        }
        return undefined;
    }, [customers, valueInProps]);

    const handleOnChange = useCallback(
        (value: PicklistValue) => onChange(value.value),
        [onChange],
    );

    const handleOnSearch = useCallback(
        (query: string) => setSearch(query),
        [],
    );

    const handleClearValue = useCallback(
        () => {
            onChange(null);
            setSearch('');
        },
        [onChange],
    );

    const createCustomer = useCallback(
        () => new Promise(
            (resolve) => {
                openDrawer({
                    mode: 'create',
                    header: 'Add a Customer',
                    customerData: undefined,
                    onRequestClose: () => {
                        closeDrawer();
                        resolve(false);
                    },
                    onCompleted: (data: Record<string, unknown>) => {
                        closeDrawer();
                        resolve(data);
                    },
                });
            },
        ),
        [closeDrawer, openDrawer],
    );

    const handleCreateCustomerClick = useCallback(
        async () => {
            if (disableCreate) return;
            const result = await createCustomer() as Customer;
            if (result) {
                onChange(result);
            }
        },
        [createCustomer, disableCreate, onChange],
    );

    const editCustomer = useCallback(
        (customer: Customer) => new Promise(
            (resolve) => {
                openDrawer({
                    mode: 'edit',
                    header: 'Edit Customer',
                    customerData: customer,
                    onRequestClose: () => {
                        closeDrawer();
                        resolve(false);
                    },
                    onCompleted: (data: Record<string, unknown>) => {
                        closeDrawer();
                        resolve(data);
                    },
                });
            },
        ),
        [closeDrawer, openDrawer],
    );

    const handleEditCustomerClick = useCallback(
        async () => {
            if (!enableEdit || !pickListvalue?.value) return;
            const selectedCustomer = pickListvalue.value as Customer;
            const result = await editCustomer(selectedCustomer);
            if (result === false) return;
            const updated = await getCustomer(agentId, selectedCustomer.id, { disableCache: true })
                .catch(() => null);
            onChange({
                ...selectedCustomer,
                ...(result as Partial<Customer> || {}),
                ...(updated || {}),
            } as Customer);
        },
        [agentId, editCustomer, enableEdit, onChange, pickListvalue],
    );

    const pickListOptions = useMemo(
        () => customers.map(
            (customer) => {
                const displayName = getDisplayName(customer);
                const initials = (
                    displayName === 'Unknown'
                        ? null
                        : getInitials(displayName)
                );
                const formattedPhoneNumber = formatIntlPhoneNumber(customer.phoneNumber);
                return (
                    <Option
                        key={`customer__${customer.id}`}
                        label={(
                            <ListTile
                                icon={(
                                    <UserAvatar
                                        initials={initials}
                                        value={customer.id}
                                        src={customer.photoURL}
                                    />
                                )}
                                title={displayName}
                                subtitle={formattedPhoneNumber}
                            />
                        )}
                        name={customer.id}
                        value={customer}
                        searchableText={`${displayName} ${formattedPhoneNumber} ${customer.phoneNumber}`}
                    />
                );
            },
        ),
        [customers],
    );

    const drawer = (
        <StyledDrawer
            {...drawerProps}
            onRequestClose={closeDrawer}
        >
            <CreateEditCustomerForm
                mode={mode === 'edit' ? 'edit' : 'create'}
                agentId={agentId}
                onRequestClose={closeDrawer}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                onCompleted={onCompleted}
                {...(customerData as object)}
            />
        </StyledDrawer>
    );

    if (pickListvalue) {
        return (
            <>
                <Container id={id} className={className}>
                    <SelectedValue
                        id={id}
                        className={className}
                        label={label}
                        required={required}
                        disabled={disabled}
                        value={pickListvalue.value}
                        compact={compact}
                        onClearValue={handleClearValue}
                    />
                    <RenderIf isTrue={enableEdit}>
                        <StyledButton
                            variant="base"
                            borderRadius="semi-square"
                            size="small"
                            onClick={handleEditCustomerClick}
                        >
                            <PencilIcon className="rainbow-m-right_x-small" />
                            Edit Customer
                        </StyledButton>
                    </RenderIf>
                </Container>
                {drawer}
            </>
        );
    }
    return (
        <>
            <Container id={id} className={className}>
                <StyledPicklist
                    name={name}
                    label={label}
                    labelAlignment="left"
                    tabIndex={tabIndex}
                    required={required}
                    placeholder={isLoadingSearchCustomers ? 'Loading...' : placeholder}
                    value={pickListvalue}
                    onChange={handleOnChange}
                    error={error}
                    disabled={disabled}
                    enableSearch
                    onSearch={handleOnSearch}
                    debounce
                    isLoading={isLoadingSearchCustomers}
                >
                    <Option label="Select Customer" variant="header" />
                    {pickListOptions}
                </StyledPicklist>
                <RenderIf isTrue={!disableCreate}>
                    <StyledButton
                        variant="base"
                        borderRadius="semi-square"
                        size="small"
                        onClick={handleCreateCustomerClick}
                        hasError={Boolean(error)}
                    >
                        <CirclePlusIcon className="rainbow-m-right_x-small" />
                        New Customer
                    </StyledButton>
                </RenderIf>
            </Container>
            {drawer}
        </>
    );
};

export default CustomerPicker;
